/* eslint-disable max-statements */

import { Box, Loader } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';

import { Batch, BatchStatusEnum, TemplateType } from '@e-origin/shared';

import { IconFactory } from '@assets';
import { IconBox, SendingIndicator } from '@components';
import { extractBatchStatusByIcon } from './utils';

interface BatchHVStatusIndicatorProps {
  batch: Batch;
}

const NO_STATISTICS_STATUSES = ['failed', 'in_progress', 'in_validation', 'scraping', 'sending', 'syncing'];

export const BatchHVStatusIndicator: React.FC<BatchHVStatusIndicatorProps> = (props) => {
  const { batch } = props;
  const batchName = batch.name;

  const hasCustomerProcessingIcon = batch.status.value === BatchStatusEnum.IN_VALIDATION;

  const allDeclarationsSentOrReleased =
    batch.statistics && (batch.statistics.sent === batch.statistics.total || batch.statistics.released === batch.statistics.total);

  const isSendingHighValueBatch = batch.templateType === TemplateType.HIGH_VALUE_H1 && batch.status.value === BatchStatusEnum.SENDING;

  const hasAnomaliesDetectedHsCodeOrDescriptionIcon =
    !hasCustomerProcessingIcon &&
    batch.status.value !== BatchStatusEnum.IN_PROGRESS &&
    batch.statistics &&
    batch.riskAnalysis &&
    ((batch.statistics?.analysisWrongDescriptionFixed !== batch.statistics?.analysisWrongDescription && batch.statistics?.analysisWrongDescription > 0) ||
      (batch.statistics?.analysisWrongHsCodeFixed !== batch.statistics?.analysisWrongHsCode && batch.statistics?.analysisWrongHsCode > 0));

  const hasDescriptionAnomaliesFixed =
    !hasCustomerProcessingIcon &&
    batch.statistics &&
    batch.riskAnalysis &&
    batch.statistics?.analysisWrongDescriptionFixed === batch.statistics?.analysisWrongDescription &&
    batch.statistics?.analysisWrongDescription > 0;

  const hasAllHSCodesFixed =
    !hasCustomerProcessingIcon &&
    batch.statistics &&
    batch.riskAnalysis &&
    batch.statistics?.analysisWrongHsCodeFixed === batch.statistics?.analysisWrongHsCode &&
    batch.statistics?.analysisWrongHsCode > 0;

  const hasAllAnomaliesHandledWithProposal =
    (hasDescriptionAnomaliesFixed && hasAllHSCodesFixed) ||
    (hasDescriptionAnomaliesFixed && !batch.statistics?.analysisWrongHsCode) ||
    (hasAllHSCodesFixed && !batch.statistics?.analysisWrongDescription);

  const hasNoAnomaliesDetected =
    !hasCustomerProcessingIcon &&
    batch.riskAnalysis &&
    batch.statistics &&
    !batch.statistics.analysisWrongDescription &&
    !batch.statistics.analysisWrongHsCode &&
    !batch.statistics.analysisWrongValue;

  const hasAnomaliesDetectedValueIcon =
    !hasCustomerProcessingIcon &&
    batch.statistics &&
    batch.riskAnalysis &&
    batch.statistics?.analysisWrongValue !== batch.statistics?.analysisWrongValueFixed &&
    batch.statistics?.analysisWrongValue > 0;

  const hasRejectedIcon = !NO_STATISTICS_STATUSES.includes(batch.status.value) && batch.statistics ? batch.statistics.rejected > 0 : false;

  const hasNotReleasedIcon = !NO_STATISTICS_STATUSES.includes(batch.status.value) && batch.statistics ? batch.statistics?.notReleased > 0 : false;

  const initialHasNotSentIcon =
    !NO_STATISTICS_STATUSES.includes(batch.status.value) &&
    !hasAnomaliesDetectedHsCodeOrDescriptionIcon &&
    !hasAllAnomaliesHandledWithProposal &&
    !hasAnomaliesDetectedValueIcon &&
    !hasNoAnomaliesDetected &&
    batch.statistics
      ? batch.statistics?.sent < batch.statistics?.total
      : false;

  const hasReleasedIcon =
    !NO_STATISTICS_STATUSES.includes(batch.status.value) && batch.statistics ? batch.statistics?.released === batch.statistics?.total : false;

  const hasInvalidationPendingIcon =
    !NO_STATISTICS_STATUSES.includes(batch.status.value) && batch.statistics ? batch.statistics?.invalidationPending > 0 : false;

  const hasInvalidatedIcon =
    !NO_STATISTICS_STATUSES.includes(batch.status.value) && batch.statistics ? batch.statistics?.invalidated === batch.statistics?.total : false;

  // gets the sent count for both PRE and POST flows
  const sentCount = batch.statistics.sent + batch.statistics.preSent;

  const isBeGateProcessing =
    batch.statistics.beGateSent === batch.statistics.total &&
    batch.statistics.beGateReleased < batch.statistics.beGateSent &&
    batch.statistics.beGateNotReleased < batch.statistics.beGateSent;

  const allSentProcessing = sentCount === batch.statistics.total && batch.statistics.released < sentCount && batch.statistics.invalidated < sentCount;

  const hasCustomsProcessingIcon =
    !NO_STATISTICS_STATUSES.includes(batch.status.value) && batch.statistics
      ? (allSentProcessing || isBeGateProcessing) && !batch.statistics.notReleased && !batch.statistics.rejected
      : false;

  const hasSendingIcon =
    !hasAnomaliesDetectedHsCodeOrDescriptionIcon &&
    !hasAllAnomaliesHandledWithProposal &&
    !hasAnomaliesDetectedValueIcon &&
    !hasNoAnomaliesDetected &&
    batch.status.value === BatchStatusEnum.SENDING &&
    batch.statistics &&
    batch.statistics?.sent < batch.statistics?.total;

  const hasScrapingIcon = batch.status.value === 'scraping';
  const hasSyncingIcon =
    !hasAnomaliesDetectedHsCodeOrDescriptionIcon &&
    !hasAllAnomaliesHandledWithProposal &&
    !hasAnomaliesDetectedValueIcon &&
    !hasNoAnomaliesDetected &&
    batch.status.value === 'syncing';

  const [hasFailedIcon, setHasFailedIcon] = useState(batch.status.value === 'failed');
  const [hasNotSentIcon, setHasNotSentIcon] = useState(initialHasNotSentIcon);
  const [hasProcessingIcon, setHasProcessingIcon] = useState(batch.status.value === 'in_progress');

  const previousBatchStatus = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (batch.status.value !== previousBatchStatus.current) {
      if (hasProcessingIcon && ['success', 'failed'].includes(batch.status.value)) {
        setHasProcessingIcon(false);

        if (batch.status.value === 'failed') {
          setHasFailedIcon(true);
        } else {
          setHasNotSentIcon(true);
        }
      }
      previousBatchStatus.current = batch.status.value;
    }
  }, [batch.status.value]);

  if (isSendingHighValueBatch || hasSendingIcon) {
    return (
      <Box display={'flex'}>
        <SendingIndicator data-pw={`batch-status-sending-${batchName}`} />
      </Box>
    );
  }

  if (hasCustomsProcessingIcon || hasInvalidationPendingIcon) {
    return (
      <Box display={'flex'}>
        <IconBox
          icon={IconFactory.processing}
          alt={'customs-processing'}
          title={extractBatchStatusByIcon(IconFactory.processing)}
          data-pw={`batch-status-processing-${batchName}`}
        />
      </Box>
    );
  }

  if (hasFailedIcon) {
    return <IconBox icon={IconFactory.failed} alt={'failed'} title={extractBatchStatusByIcon(IconFactory.failed)} />;
  }

  return (
    <Box display={'flex'}>
      {hasNotSentIcon && (
        <IconBox
          icon={IconFactory.notSent}
          alt={'not-sent'}
          title={extractBatchStatusByIcon(IconFactory.notSent)}
          data-pw={`batch-status-not-sent-${batchName}`}
        />
      )}
      {hasReleasedIcon && (
        <IconBox
          icon={IconFactory.released}
          alt={'released'}
          title={extractBatchStatusByIcon(IconFactory.released)}
          data-pw={`batch-status-released-${batchName}`}
        />
      )}
      {hasInvalidatedIcon && (
        <IconBox
          icon={IconFactory.invalidated}
          alt={'invalidated'}
          title={extractBatchStatusByIcon(IconFactory.invalidated)}
          data-pw={`batch-status-invalidated-${batchName}`}
        />
      )}
      {hasRejectedIcon && (
        <IconBox
          icon={IconFactory.rejected}
          alt={'rejected'}
          title={extractBatchStatusByIcon(IconFactory.rejected)}
          data-pw={`batch-status-rejected-${batchName}`}
        />
      )}
      {hasNotReleasedIcon && (
        <IconBox
          icon={IconFactory.notReleased}
          alt={'not-released'}
          title={extractBatchStatusByIcon(IconFactory.notReleased)}
          data-pw={`batch-status-not-released-${batchName}`}
        />
      )}
      {hasNoAnomaliesDetected && !hasProcessingIcon && (
        <IconBox
          icon={IconFactory.noSentAnomalies}
          alt={'no-anomalies-detected-value'}
          title={extractBatchStatusByIcon(IconFactory.noSentAnomalies)}
          data-pw={`batch-status-no-anomalies-detected-${batchName}`}
        />
      )}
      {!isSendingHighValueBatch && !allDeclarationsSentOrReleased && hasAnomaliesDetectedValueIcon && !hasProcessingIcon && (
        <IconBox
          icon={IconFactory.anomalyValue}
          alt={'anomalies-detected-value'}
          title={extractBatchStatusByIcon(IconFactory.anomalyValue)}
          data-pw={`batch-status-anomalies-detected-${batchName}`}
        />
      )}
      {!isSendingHighValueBatch && !allDeclarationsSentOrReleased && hasAnomaliesDetectedHsCodeOrDescriptionIcon && !hasProcessingIcon && (
        <IconBox
          icon={IconFactory.anomaliesHSCodeOrDescription}
          alt={'anomalies-hs-code-or-description'}
          title={extractBatchStatusByIcon(IconFactory.anomaliesHSCodeOrDescription)}
          data-pw={`batch-status-anomalies-hs-code-or-description-${batchName}`}
        />
      )}
      {hasAllAnomaliesHandledWithProposal && !hasProcessingIcon && (
        <IconBox
          icon={IconFactory.anomaliesHandled}
          alt={'all-anomalies-handled-with-proposal'}
          title={extractBatchStatusByIcon(IconFactory.anomaliesHandled)}
          data-pw={`batch-status-anomalies-handled-${batchName}`}
        />
      )}
      {hasSyncingIcon && (
        <IconBox
          icon={IconFactory.customsSync}
          alt={'customs-sync'}
          title={extractBatchStatusByIcon(IconFactory.customsSync)}
          data-pw={`batch-status-syncing-${batchName}`}
        />
      )}
      {((hasProcessingIcon && !hasAnomaliesDetectedHsCodeOrDescriptionIcon) || hasScrapingIcon) && (
        <Loader size={24} data-pw={`batch-status-loading-${batchName}`} />
      )}
      {hasCustomerProcessingIcon && (
        <IconBox
          icon={IconFactory.customsProcessing}
          alt={'customer-processing'}
          title={extractBatchStatusByIcon(IconFactory.customsProcessing)}
          data-pw={`batch-status-customs-processing-${batchName}`}
        />
      )}
      {!hasFailedIcon &&
        !hasCustomerProcessingIcon &&
        !hasNotSentIcon &&
        !hasReleasedIcon &&
        !hasInvalidatedIcon &&
        !hasRejectedIcon &&
        !hasNotReleasedIcon &&
        !hasCustomsProcessingIcon &&
        !hasSendingIcon &&
        !hasSyncingIcon &&
        !hasProcessingIcon &&
        !hasScrapingIcon &&
        !hasAnomaliesDetectedHsCodeOrDescriptionIcon &&
        !hasAllAnomaliesHandledWithProposal &&
        !hasAnomaliesDetectedValueIcon &&
        !hasNoAnomaliesDetected && (
          <IconBox
            icon={IconFactory.processing}
            alt={'customs-processing'}
            title={extractBatchStatusByIcon(IconFactory.processing)}
            data-pw={`batch-status-processing-${batchName}`}
          />
        )}
    </Box>
  );
};
